import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  clearSession,
  currentSession,
  updateSessionUser,
  type SessionUser,
} from '@/api/client'
import * as authApi from '@/api/auth'
import * as superuserApi from '@/api/superuser'
import { peutUtiliserSuperuser } from '@/navigation'
import { realtime } from '@/api/realtime'

/**
 * Source de vérité de l'identité côté interface. Le jeton, lui, reste dans
 * `api/client` : un store qui porterait aussi les jetons obligerait le client
 * HTTP à dépendre de Pinia, et donc à monter l'application entière pour tester
 * un appel réseau.
 */
export const useAuthStore = defineStore('auth', () => {
  // Hydratation depuis la session persistée : après un F5, l'utilisateur est
  // déjà connu avant tout appel réseau — pas d'écran de connexion qui clignote.
  const user = ref<SessionUser | null>(currentSession()?.user ?? null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  /** Autorisation grossière, alignée sur la colonne `role` en base. */
  const canWrite = computed(
    () => user.value !== null && user.value.role !== 'viewer',
  )
  const isAdmin = computed(
    () => user.value?.role === 'owner' || user.value?.role === 'admin',
  )

  /**
   * Accès au périmètre PLATEFORME (organisation de gestion).
   *
   * Miroir de `canUseSuperuser` côté serveur, exposé par le store pour que la
   * barre latérale n'ait pas à recomposer la règle. Sert à AFFICHER, jamais à
   * autoriser : chaque route du périmètre revérifie le droit.
   */
  const canSuperuser = computed(() => peutUtiliserSuperuser(user.value))

  /**
   * « Jump » backoffice en cours (E15-1), ou `null`. `?? null` : une session
   * persistée avant ce champ n'en a pas — elle vaut « session ordinaire »
   * jusqu'au prochain `/auth/me`, jamais « indéfini ».
   */
  const jump = computed(() => user.value?.jump ?? null)

  /**
   * Applique un profil de session rendu par le serveur après une BASCULE
   * d'organisation (entrée chez un client, retour chez soi).
   *
   * Trois effets, tous nécessaires : le store (l'interface), la session
   * persistée (le garde du routeur la lit, et il s'exécute avant tout
   * composant — sans elle, la barre latérale montrerait un périmètre que le
   * garde refuse), et le canal temps réel — ouvert sur l'organisation
   * EFFECTIVE au moment de la poignée de main : le rouvrir est le seul moyen
   * de recevoir les événements de là où l'on est désormais, et de ne plus
   * recevoir ceux de là où l'on n'est plus.
   */
  function appliquerProfil(profil: SessionUser): void {
    user.value = profil
    updateSessionUser(profil)
    realtime.disconnect()
    realtime.connect()
  }

  /**
   * Entre dans l'organisation d'un client depuis l'annuaire plateforme.
   *
   * Le droit est vérifié SERVEUR et revalidé à chaque requête suivante ; en cas
   * de refus, l'erreur remonte telle quelle à l'écran appelant (qui a une
   * notification à afficher), rien n'est appliqué localement.
   */
  async function entrerChez(organisationId: string): Promise<void> {
    appliquerProfil(await superuserApi.jumpOrganisation(organisationId))
  }

  /** Revient chez soi : c'est le serveur qui efface l'état ; le profil rendu n'a plus de `jump`. */
  async function revenirChezSoi(): Promise<void> {
    appliquerProfil(await authApi.quitterJump())
  }


  async function logout(): Promise<void> {
    // Fermeture AVANT l'appel réseau : la déconnexion doit couper le flux même
    // si la révocation côté serveur échoue.
    realtime.disconnect()
    await authApi.logout()
    user.value = null
  }

  /**
   * Coupe la session SANS appeler le serveur. Utilisé par le gestionnaire de
   * 401 du client HTTP : le jeton est déjà refusé, un appel de plus ne ferait
   * qu'échouer.
   */
  function forgetLocally(): void {
    realtime.disconnect()
    clearSession()
    user.value = null
  }

  /**
   * Revalide la session auprès du serveur. Appelé au démarrage quand une
   * session locale existe : un jeton révoqué côté serveur (mot de passe changé
   * ailleurs, compte désactivé) doit déconnecter, pas afficher une interface
   * qui échouera à chaque action.
   */
  // Le serveur ferme le canal en « organisation changée » à chaque fin de jump
  // backoffice — et ne le dit nulle part ailleurs : les requêtes HTTP
  // continuent de répondre 200, dans l'organisation d'origine. Sans cette
  // relecture, le bandeau continuerait d'affirmer qu'on est chez le client
  // pendant que tout s'écrit chez betool.
  realtime.écouterChangementOrganisation(() => {
    void refreshUser()
  })

  async function refreshUser(): Promise<void> {
    if (!currentSession()) return
    try {
      const frais = await authApi.me()
      user.value = frais
      // La session persistée porte aussi l'utilisateur, et le garde du routeur
      // la lit : sans cette mise à jour, un droit tout juste accordé (ou retiré)
      // serait visible dans l'interface et ignoré par le garde.
      updateSessionUser(frais)
      // Session valide au chargement de la page : on rétablit le canal, sinon
      // un rafraîchissement d'onglet couperait le temps réel sans rien dire.
      realtime.connect()
    } catch {
      forgetLocally()
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    canWrite,
    isAdmin,
    canSuperuser,
    jump,
    entrerChez,
    revenirChezSoi,
    logout,
    forgetLocally,
    refreshUser,
  }
})
